import React, { useState } from 'react';
import { BottomNav } from './components/BottomNav';
import type { ScreenId } from './components/BottomNav';
import { MetaLabel } from './components/MetaLabel';
import { ShootProvider, useShoot } from './contexts/ShootContext';
import { Today } from './pages/Today';
import { Schedule } from './pages/Schedule';
import { Crew } from './pages/Crew';
import { Shots } from './pages/Shots';
import { Kit } from './pages/Kit';

/**
 * The crew-facing call sheet.
 *
 * Called "Shoot Day" until 13 Sep 2026, when Tom asked for one name for one
 * thing — "lets only call it a call sheet". Only the wording changed: the URL
 * and the tokens are untouched, so every link already sitting in a WhatsApp
 * group still opens exactly the same sheet.
 */

/** Client links (added 9 Sep 2026) only ever see these three screens —
    Shots and Kit are a crew-only checklist, not part of the client view. */
const CLIENT_SCREENS: ScreenId[] = ['today', 'schedule', 'crew'];
const ALL_SCREENS: ScreenId[] = ['today', 'schedule', 'crew', 'shots', 'kit'];
function AppShell() {
  const [screen, setScreen] = useState<ScreenId>('today');
  const {
    loading,
    tokenGivenButNotFound,
    role,
    production,
    shotSections,
    kitGroups
  } = useShoot();

  // 9 Sep 2026, at Tom's request: an empty shot list is just noise on set,
  // so the Shots tab drops out entirely (not shown disabled) whenever no
  // shot has been added — the same "leave it out, don't grey it out"
  // treatment CLIENT_SCREENS already gives Shots/Kit for clients.
  //
  // 13 Sep 2026: Kit now gets the same rule, and the same rule is also how
  // the call sheet's segment switches reach this app. Tom can switch Shots
  // or Kit off for a day in the builder — a live broadcast wants the
  // schedule and the contacts, not a shot list — and get_shoot_day_by_token
  // then withholds that segment's rows outright rather than sending them for
  // the app to hide. So a switched-off segment arrives here as an empty list
  // and drops its tab by exactly the same path an empty one always has.
  // Nothing is deleted: the list is still in the builder for the next day
  // that wants it.
  const hasShots = shotSections.some(section => section.items.length > 0);
  const hasKit = kitGroups.some(group => group.items.length > 0);
  const visibleScreens = (role === 'client' ? CLIENT_SCREENS : ALL_SCREENS).filter(id => {
    if (id === 'shots') return hasShots;
    if (id === 'kit') return hasKit;
    return true;
  });
  // Guards against landing on a screen this link doesn't offer — including
  // the case that matters now that segments can be switched off mid-day:
  // someone sitting on the Kit tab when it's switched off is moved back to
  // Today on the next poll rather than left on a screen the nav no longer
  // reaches.
  const activeScreen = visibleScreens.includes(screen) ? screen : 'today';
  const navigate = (next: ScreenId) => {
    setScreen(next);
  };
  if (loading) {
    return <div className="flex h-full w-full items-center justify-center bg-ink" data-id="element-0" data-mp="2stpj16xo2n0c">
        <MetaLabel tone="dim" data-id="element-1" data-mp="18c6kxv857cao">Loading call sheet…</MetaLabel>
      </div>;
  }
  if (tokenGivenButNotFound) {
    return <div className="flex h-full w-full flex-col items-center justify-center gap-3 bg-ink px-8 text-center" data-id="element-2" data-mp="1d6ayixbwyikq">
        <MetaLabel tone="dim" data-id="element-3" data-mp="13eofietc3f7f">Call Sheet</MetaLabel>
        <p className="font-display text-2xl font-semibold leading-tight text-fg" data-id="element-4" data-mp="13f7v3ovihria">
          This link wasn't found.
        </p>
        <p className="max-w-xs text-[13px] leading-relaxed text-muted" data-id="element-5" data-mp="1r2g0e6sxx0kb">
          It may have been generated for a different day, or the link is incomplete. Ask your
          producer to resend it.
        </p>
      </div>;
  }
  return <>
      <aside className="hidden flex-1 flex-col justify-between px-12 py-14 lg:flex" data-id="element-6" data-mp="1m44rbyagyu35">
        <div data-id="element-7" data-mp="6nw43uhcibbz">
          <MetaLabel tone="dim" data-id="element-8" data-mp="6ncmkevlxoyh">{production.studio}</MetaLabel>
          <p className="mt-6 font-display text-[92px] font-semibold leading-[0.85] tracking-display text-fg" data-id="element-9" data-mp="aq0umkchyrue">
            Call
            <br data-id="element-10" data-mp="qxfazzaktjmi" />
            Sheet
          </p>
          <p className="mt-8 max-w-sm text-[15px] leading-relaxed text-muted" data-id="element-11" data-mp="qz8dd2e5phop">
            The running order, the contacts and the kit — live, and carried on set.
          </p>
        </div>

        <dl className="max-w-sm border-t border-line" data-id="element-12" data-mp="h59hgl4oeu57">
          {[{
          label: 'Production',
          value: production.title
        }, {
          label: 'Location',
          value: production.location
        }, {
          label: 'Date',
          value: production.date
        }].map(row => <div key={row.label} className="flex items-baseline justify-between gap-8 border-b border-line py-3" data-id="element-13" data-mp="14y81m8xrbukc">
              <dt className="text-[10px] font-medium uppercase tracking-label text-dim" data-id="element-14" data-mp="zw0ohg16y8gf">{row.label}</dt>
              <dd className="text-[14px] text-fg" data-id="element-15" data-mp="vybam0nwajm4">{row.value}</dd>
            </div>)}
        </dl>
      </aside>

      <div className="relative flex h-full w-full flex-col overflow-hidden border-line bg-ink lg:max-w-pane lg:border-x" data-id="element-16" data-mp="3gmh6gfbk5g9c">
        <main className="no-scrollbar flex-1 overflow-y-auto" data-id="element-17" data-mp="1w4yj9ynknpyg">
          {activeScreen === 'today' ? <Today data-id="element-18" data-mp="205qswt0cvwv9" /> : null}
          {activeScreen === 'schedule' ? <Schedule data-id="element-19" data-mp="206a5du78lt1w" /> : null}
          {activeScreen === 'crew' ? <Crew data-id="element-20" data-mp="254v1bhfghqkj" /> : null}
          {activeScreen === 'shots' ? <Shots data-id="element-21" data-mp="253a8bw8sifos" /> : null}
          {activeScreen === 'kit' ? <Kit data-id="element-22" data-mp="29wv89tty1jqm" /> : null}
        </main>

        <BottomNav active={activeScreen} onNavigate={navigate} visible={visibleScreens} data-id="element-23" data-mp="29xwmu8a64od3" />
      </div>
    </>;
}
export function App() {
  return <ShootProvider data-id="element-24" data-mp="2jt81kqlktvku">
      <div className="flex h-[100dvh] w-full justify-center overflow-hidden bg-ink" data-id="element-25" data-mp="2jsomdhnb9lai">
        <div className="flex w-full max-w-[1180px]" data-id="element-26" data-mp="2nwnyzp5gzzyl">
          <AppShell data-id="element-27" data-mp="2supfc2bihl6d" />
        </div>
      </div>
    </ShootProvider>;
}